import { useMemo } from 'react';

import type { Maybe } from '@/shared/libs';
import { useDeals } from '@entities/Deal';
import { useCatalogOffers } from '@entities/Offer';
import { UserRoleEnum } from '@entities/User';

const CATALOG_SEEN_KEY = (role: string): string => `psp_catalog_seen_${role}`;

export const markCatalogSeen = (role: Maybe<string>): void => {
  if (!role) {
    return;
  }

  try {
    window.localStorage.setItem(
      CATALOG_SEEN_KEY(role),
      new Date().toISOString(),
    );
    window.dispatchEvent(new Event('psp:catalog-seen'));
  } catch {
    // ignore storage errors
  }
};

export interface NavBadges {
  catalog: number;
  deals: number;
}

export const useNavBadges = (role: Maybe<UserRoleEnum | string>): NavBadges => {
  const dealsQuery = useDeals({
    enabled: Boolean(role),
    limit: 100,
    page: 1,
  });
  const catalogQuery = useCatalogOffers({
    enabled: role === UserRoleEnum.Agent || role === UserRoleEnum.Merchant,
    limit: 100,
    page: 1,
  });

  return useMemo(() => {
    const deals =
      dealsQuery.data?.items.filter(
        (deal) => deal.status === 'in_review' || deal.status === 'draft',
      ).length ?? 0;

    let catalog = 0;

    if (role === UserRoleEnum.Agent || role === UserRoleEnum.Merchant) {
      const seenRaw = window.localStorage.getItem(CATALOG_SEEN_KEY(role));
      const seenTs = seenRaw ? new Date(seenRaw).getTime() : 0;

      catalog =
        catalogQuery.data?.items.filter((offer) => {
          const created = new Date(offer.created_at).getTime();
          const updated = new Date(offer.updated_at).getTime();
          const isNew = created > seenTs;
          const pausedSince =
            offer.status === 'paused' &&
            updated > seenTs &&
            Math.abs(updated - created) > 1000;

          return isNew || pausedSince;
        }).length ?? 0;
    }

    return { catalog, deals };
  }, [catalogQuery.data?.items, dealsQuery.data?.items, role]);
};
